import { z } from 'zod';

import {
  entityTypeAnchorFields,
  readEntityTypeAnchor,
} from './entity-type-anchor';
import {
  createCustomPageBuiltResult,
  createCustomPageExpiredResult,
  createCustomPageNoDigestsResult,
  createCustomPageOfferedResult,
  createCustomPagePartialResult,
  createCustomPageUnconfirmedResult,
  turnAbortedNoOpResult,
} from './tool-result-copy';
import type { ClientToolDefinition } from './types';

const schema = z
  .object({
    prompt: z.string(),
    ...entityTypeAnchorFields,
    narration: z.string().optional(),
  })
  .passthrough();

export const createCustomPageTool: ClientToolDefinition<typeof schema> = {
  schema,
  // Param-narration offer — see navigate's narrationPolicy note.
  narrationPolicy: 'drop',
  async run(input, { deps, emit, signal }) {
    const { prompt, narration } = input;
    const entityType = readEntityTypeAnchor(input);
    const result = await deps.requestCreateCustomPage(
      { prompt, entityType },
      emit
    );
    // Re-check after the await: in BUILD-HERE mode this routes through
    // `emitPageGenAndCollect`, which RESOLVES rather than rejects on abort, so
    // the body always resumes — and the narration append below writes a
    // clickable build chip that an engine-unmount abort has no rewind for (see
    // ClientToolContext.signal).
    if (signal?.aborted) {
      return turnAbortedNoOpResult;
    }
    if ('offered' in result) {
      // OFFER mode (default): the narration's `[phrase](offer:build)` marker
      // becomes an inline trigger (suppressing the redundant build-confirm
      // chip), appended BEFORE the chip so the text reads above the button.
      // Only here — in BUILD-HERE mode the build already ran, so the same
      // marker would offer to build a dashboard that is already building.
      if (narration) {
        deps.appendAssistantText(narration, { build: result.messageId });
      }
      // Clicking the chip navigates to an empty draft and resumes the turn in
      // BUILD-HERE mode (below), so the loop never builds on this offer.
      return createCustomPageOfferedResult;
    }
    // BUILD-HERE mode: the user confirmed; the CREATE page-gen ran on the
    // current draft. Narrate ONLY the actual widgets the build collected.
    if (!result.ok) {
      if (result.failureReason === 'expired') {
        return createCustomPageExpiredResult;
      }
      return createCustomPageUnconfirmedResult;
    }
    const { digests, expectedCount } = result;
    if (digests.length === 0) {
      return createCustomPageNoDigestsResult;
    }
    if (expectedCount !== undefined && digests.length < expectedCount) {
      return createCustomPagePartialResult(expectedCount, digests);
    }
    return createCustomPageBuiltResult(digests);
  },
};
